export class UnauthorizedHandledError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnauthorizedHandledError';
  }
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export const CONNECTION_ERROR_MESSAGE =
  'Không thể kết nối hệ thống. Vui lòng kiểm tra kết nối mạng và thử lại sau.';

export const GENERIC_API_ERROR_MESSAGE =
  'Đã xảy ra lỗi. Vui lòng thử lại sau.';

const TECHNICAL_ERROR_PATTERN =
  /(https?:\/\/|API_URL|API Error|Network request failed|Failed to fetch|NetworkError|AbortError|ECONN(?:REFUSED|RESET)|status provided \(0\)|Failed to construct|JSON parse error|Unexpected character|Unexpected token|SyntaxError|is not valid JSON|<\!DOCTYPE|<html)/i;

/**
 * Chỉ trả về nội dung phù hợp để hiển thị cho người dùng.
 * Lỗi nghiệp vụ từ backend vẫn được giữ nguyên, còn URL và lỗi kết nối kỹ thuật
 * được thay bằng thông báo chung.
 */
export function getUserFriendlyErrorMessage(
  error: unknown,
  fallback: string = GENERIC_API_ERROR_MESSAGE,
): string {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';

  if (!message) {
    return fallback;
  }

  if (message === CONNECTION_ERROR_MESSAGE || TECHNICAL_ERROR_PATTERN.test(message)) {
    return CONNECTION_ERROR_MESSAGE;
  }

  return message;
}

export abstract class BaseApiService {
  protected abstract getBaseURL(): string;
  protected abstract getAuthToken(): Promise<string | null>;
  protected abstract handleUnauthorized(message?: string): Promise<void>;

  protected async request<T>(
    endpoint: string,
    options?: RequestInit,
    requireAuth: boolean = true
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    if (requireAuth) {
      const token = await this.getAuthToken();
      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    }

    const fullUrl = `${this.getBaseURL()}${endpoint}`;

    try {
      let response: Response;
      try {
        response = await fetch(fullUrl, {
          ...options,
          headers,
          credentials: 'include', // Quan trọng: Gửi credentials (cookies) cho CORS
        });
      } catch (fetchError: any) {
        // Bắt lỗi "Failed to construct 'Response'" với status 0
        // Lỗi này xảy ra khi whatwg-fetch cố tạo Response với status 0 (network error/timeout/abort)
        if (
          fetchError.message?.includes('Failed to construct') ||
          fetchError.message?.includes('status provided (0)') ||
          fetchError.message?.includes('outside the range')
        ) {
          throw new Error(CONNECTION_ERROR_MESSAGE);
        }
        throw fetchError;
      }

      // Kiểm tra response status hợp lệ (status 0 thường là network error hoặc abort)
      if (response.status === 0) {
        throw new Error(CONNECTION_ERROR_MESSAGE);
      }

      if (response.status === 401) {
        let errorMessage = 'Unauthorized';
        try {
          const errorData = await response.json().catch(() => ({}));
          errorMessage = getUserFriendlyErrorMessage(
            errorData.message,
            'Unauthorized',
          );
        } catch {
          // Ignore JSON parse errors
        }

        if (requireAuth) {
          await this.handleUnauthorized(errorMessage);
          throw new UnauthorizedHandledError(errorMessage);
        }
        throw new Error(errorMessage);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiRequestError(
          getUserFriendlyErrorMessage(errorData.message),
          response.status,
        );
      }

      const text = await response.text();
      if (!text) {
        return {} as T;
      }

      const trimmed = text.trim();
      if (
        trimmed.startsWith('<') ||
        trimmed.startsWith('<!DOCTYPE') ||
        trimmed.startsWith('<html')
      ) {
        throw new Error(CONNECTION_ERROR_MESSAGE);
      }

      try {
        return JSON.parse(text);
      } catch {
        throw new Error(CONNECTION_ERROR_MESSAGE);
      }
    } catch (error: any) {
      const isNetworkError =
        error.name === 'AbortError' ||
        error.name === 'SyntaxError' ||
        error.message?.includes('Network request failed') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('Failed to construct') ||
        error.message?.includes('status provided (0)') ||
        error.message?.includes('outside the range') ||
        error.message?.includes('JSON parse error') ||
        error.message?.includes('Unexpected character') ||
        error.message?.includes('Unexpected token') ||
        error.message?.includes('is not valid JSON');

      if (isNetworkError) {
        throw new Error(CONNECTION_ERROR_MESSAGE);
      }

      const friendlyMessage = getUserFriendlyErrorMessage(error);
      if (friendlyMessage !== error?.message) {
        throw new Error(friendlyMessage);
      }

      throw error;
    }
  }

  async get<T>(endpoint: string, requireAuth: boolean = true): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' }, requireAuth);
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    requireAuth: boolean = true
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      requireAuth
    );
  }

  async put<T>(
    endpoint: string,
    data?: unknown,
    requireAuth: boolean = true
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      requireAuth
    );
  }

  async patch<T>(
    endpoint: string,
    data?: unknown,
    requireAuth: boolean = true
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      requireAuth
    );
  }

  async delete<T>(endpoint: string, requireAuth: boolean = true): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }, requireAuth);
  }
}

export function isErrorHandled(error: any): boolean {
  return error instanceof UnauthorizedHandledError || error?.name === 'UnauthorizedHandledError';
}
